/* eslint-disable @typescript-eslint/no-empty-function */
/* eslint-disable max-len */
import React, { FC, useEffect, useState } from 'react';
// import fuzzysort from 'fuzzysort';
import { useLangContext } from '@aarhus-university/au-lib-react-components/src/lib/context';
import AutoSuggestComponent from '@aarhus-university/au-lib-react-components/src/components/AUAutoSuggestComponent';
import AUSpinnerComponent from '@aarhus-university/au-lib-react-components/src/components/AUSpinnerComponent';
import { useGetMapDataQuery, useLazySearchPersonQuery } from '../services/api';
import { createMap, setOverlay } from '../lib/maps';
import BuildingCardComponent from './BuildingCardComponent';
import VCardComponent from './VCardComponent';
import SuggestionComponent from './SuggestionComponent';
import { isNumeric, isSpecialBuildingNumber } from '../lib/helpers';

const centerLat = 56.168995;
const centerLng = 10.203113;

const generateDirectLink = (result: IMapLocation) => `?b=${result.id}`;

type Props = {
  token: string;
}

/*
// https://github.com/farzher/fuzzysort
const getMatchScore = (query: string, person: IEmployee) => {
  const results = fuzzysort.go(query, [person], { key: 'fullName' });
  if (results.length === 1) {
    return results[0].score;
  }
  return 0;
};
*/

const getSuggestions = (
  value: string,
  collection: IMapLocation[],
  callback: (suggestions: ISuggestion[]) => void,
  searchPersons: (callback: (suggestions: ISuggestion[]) => void) => void,
): void => {
  if (typeof value === 'string') {
    const inputValue = value.trim().toLowerCase();
    const inputLength = inputValue.length;
    const isNumber = isNumeric(inputValue);

    if (inputLength < 3 && !isNumber) {
      // Gør ingenting, hvis der er TASTET mindre end to tegn
      callback([]);
    } else if (inputLength > 2 || isNumber || isSpecialBuildingNumber(inputValue)) {
      // Hvis der er TASTET mere end to tegn,
      // eller hvis input er et tal/id

      const locationResults = collection.reduce((acc: IMapLocation[], item) => {
        const loc = { ...item };
        let found = loc.id.toLocaleLowerCase() === inputValue;
        if (!found) {
          loc.names.forEach((n, index) => {
            if (!found && n.name.toLowerCase().indexOf(inputValue) > -1) {
              // Hvis der er en match på navnet, vises det
              loc.suggestion = `${loc.names[0].name}${index > 0 ? ` (${loc.names[index].name})` : ''}`;
              found = true;
            }
          });
        } else {
          loc.suggestion = loc.names[0].name;
        }
        if (found) {
          acc.push(loc);
        }
        return acc;
      }, []);

      if (locationResults.length > 0) {
        callback(locationResults.slice(0, 20)); // Forsøg: vi viser kun 20 bygninger i suggestions
        // Hvis vi ikke har fundet noget i data, søger vi efter en person
      } else if (typeof callback === 'function') {
        /*
        if (isNumber || inputValue.includes('@')) {
          url = `${url}&exact=true`;
        }
          */
        searchPersons(callback);
      }
    }
  }
};

const getCard = (result: IMapLocation | IEmployee) => {
  const person = result as IEmployee;
  if (person.departments) {
    const pureUrl = person.departments.length > 0 ? `http://au.dk/${person.departments[0].email}` : '';
    const mailTo = person.departments.length > 0 ? `mailto:${person.departments[0].email}` : '';
    const { fullName, photo, departments } = person;

    return (
      <VCardComponent
        key={result.id}
        pureUrl={pureUrl}
        mailTo={mailTo}
        fullName={fullName}
        photo={photo}
        departments={departments}
      />
    );
  }

  const bld = result as IMapLocation;

  return (
    <BuildingCardComponent
      key={result.id}
      names={bld.names}
      address={bld.address}
    />
  );
};

const BuildingMapComponent: FC<Props> = ({ token }) => {
  const lang = useLangContext();
  const [mapkitMap, setMapkitMap] = useState<mapkit.Map | null>(null);
  const [locations, setLocations] = useState<IMapLocation[]>([]);
  const [results, setResults] = useState<IMapLocation[] | IEmployee[]>([]);
  const { data, isLoading, isSuccess } = useGetMapDataQuery();
  const [trigger] = useLazySearchPersonQuery();
  useEffect(() => {
    if (isSuccess) {
      const map = createMap(token, centerLat, centerLng, 'map');
      setMapkitMap(map);

      // Get parameter b from query string
      const param = new URLSearchParams(window.location.search).get('b');
      if (param) {
        getSuggestions(
          param,
          data || [],
          (suggestions: ISuggestion[]) => {
            if (suggestions.length > 0) {
              setResults([suggestions[0] as IMapLocation]);
            }
          },
          (c: (suggestions: ISuggestion[]) => void) => {
            trigger(param).unwrap().then((persons) => {
              if (persons.length > 0) {
                c(persons.map((d) => ({ ...d, suggestion: d.fullName })));
              } else {
                c([]);
              }
            });
          },
        );
      }

      return () => {
        map.destroy();
      };
    }

    return () => {
      // eslint-disable-next-line no-console
      console.log('MapComponent unmounted');
    };
  }, [data, isSuccess]);

  useEffect(() => {
    if (mapkitMap) {
      locations.forEach((loc) => {
        setOverlay(mapkitMap, loc);
      });
    }
  }, [locations]);

  useEffect(() => {
    const newLocations: IMapLocation[] = [];
    results.forEach((res: IMapLocation | IEmployee) => {
      // Hvis bygning
      const bld = res as IMapLocation;
      if (bld.latitude && bld.longitude) {
        newLocations.push(bld);
      } else {
        // Hvis person
        const person = res as IEmployee;
        if (person.departments.length > 0) {
          const department = person.departments[0];
          if (department.address?.building) {
            const building = data?.find((b) => b.id === department.address.building.id);
            if (building) {
              newLocations.push(building);
            }
          }
        }
      }
    });
    setLocations(newLocations);
  }, [results]);

  const renderResults = results.map((r: IMapLocation | IEmployee) => getCard(r));

  let mapColumnClass = 'column large-12 medium-12 small-12';
  if (results.length > 0) {
    mapColumnClass = 'column large-9 medium-9 small-12';
  }
  const resultColumnClass = 'column large-3 medium-3 small-12';

  return (
    <AUSpinnerComponent loaded={!isLoading}>
      <div className="row">
        <div className={mapColumnClass}>
          <form
            key="search"
            className="form"
            onSubmit={(e) => {
              e.preventDefault();
            }}
          >
            <div className="au-map__search">
              <div className="form__field">
                <AutoSuggestComponent
                  id="au-map-buildings-autocomplete"
                  placeholder={lang === 'da' ? 'Søg efter bygning eller person' : 'Search for building or person'}
                  setQuery={() => { }}
                  setResults={(res: IEmployee[] | IMapLocation[]) => {
                    if (res.length > 0) {
                      setResults(res);
                    }
                  }}
                  collection={data || []}
                  getSuggestions={(
                    value: string,
                    collection: IMapLocation[],
                    callback: (suggestions: ISuggestion[]) => void,
                  ) => getSuggestions(
                    value,
                    collection,
                    callback,
                    (c: (suggestions: ISuggestion[]) => void) => {
                      trigger(value).unwrap().then((persons) => {
                        if (persons.length > 0) {
                          // const sortedData = persons.sort((a, b) => (getMatchScore(value, a) > getMatchScore(value, b) ? -1 : 1));
                          c(persons.map((d) => ({ ...d, suggestion: d.fullName })));
                        } else {
                          c([]);
                        }
                      });
                    },
                  )}
                  getSuggestionValue={(suggestion: ISuggestion) => suggestion.suggestion}
                  renderSuggestion={(suggestion: IMapLocation | IEmployee) => <SuggestionComponent result={suggestion} />}
                  clearInput
                  theme={{
                    container: 'react-autosuggest__container',
                    containerOpen: 'react-autosuggest__container--open',
                    input: 'react-autosuggest__input',
                    inputOpen: 'react-autosuggest__input--open',
                    inputFocused: 'react-autosuggest__input--focused',
                    suggestionsContainer: 'react-autosuggest__suggestions-container',
                    suggestionsContainerOpen: 'react-autosuggest__suggestions-container--open',
                    suggestionsList: 'react-autosuggest__suggestions-list',
                    suggestion: 'react-autosuggest__suggestion',
                    suggestionFirst: 'react-autosuggest__suggestion--first',
                    suggestionHighlighted: 'react-autosuggest__suggestion--highlighted',
                    sectionContainer: 'react-autosuggest__section-container',
                    sectionContainerFirst: 'react-autosuggest__section-container--first',
                    sectionTitle: 'react-autosuggest__section-title',
                  }}
                />
              </div>
            </div>
          </form>
          <div id="map" />
          {
            (results.length === 1 && (results[0] as IMapLocation).type === '0') && (
              <a className="a--text-link" href={`${window.location.href.replace(window.location.search, '')}${generateDirectLink(results[0] as IMapLocation)}`}>{lang === 'da' ? 'Direkte link til kortvisning med den valgte bygning' : 'Direct link to the map with the selected building'}</a>
            )
          }
        </div>
        {
          (results.length > 0) && (
            <div className={resultColumnClass}>
              {renderResults}
            </div>
          )
        }
      </div>
    </AUSpinnerComponent>
  );
};

BuildingMapComponent.displayName = 'BuildingMapComponent';
export default BuildingMapComponent;
